import { createSelector } from 'reselect';
import { filterAssignmentsByType } from '../utils/permissionsHelpers';
import { assignmentTypes } from '../shared/authorization/assignmentTypes';
import { uniq } from '../shared/uniq';
import { getAssignmentKey } from './utils';
import { userRoles } from '@common/constants/sharedConstants';
import { ALL_ASSIGNMENTS_CODE } from './constants';

export const getPermissionsByRoleAndClient = (selectedClientId, selectedRole, permissions) => {
  const defaultPermissions = permissions[0].availablePermissions.user;

  if (!selectedRole || !Number.isInteger(selectedClientId)) {
    return defaultPermissions;
  }

  const availablePermissions = permissions.find(({ clientId }) => clientId === selectedClientId);

  return (availablePermissions && availablePermissions.availablePermissions[selectedRole])
    || defaultPermissions;
};
export const getPermissionsByRoleAndClientSelector = createSelector(
  ({ selectedClient }) => selectedClient && selectedClient.clientId,
  ({ selectedRole }) => selectedRole && selectedRole.name,
  ({ permissions }) => permissions,
  getPermissionsByRoleAndClient
);

export const getRolesByClient = createSelector(
  ({ selectedClient }) => selectedClient && selectedClient.clientId,
  ({ rolesOptions }) => rolesOptions,
  (selectedClientId, rolesOptions) => {
    const defaultRoles = rolesOptions[0].roles;

    if (!Number.isInteger(selectedClientId)) {
      return defaultRoles;
    }

    const roles = rolesOptions.find(({ clientId }) => clientId === selectedClientId);

    return (roles && roles.roles) || defaultRoles;
  }
);

export const isAllowedToEditPrivilegesAndPermissions = ({ role, selectedClient, rolesOptions }) => {
  if (!selectedClient) {
    return false;
  }

  if (!role) {
    return true;
  }

  const roles = getRolesByClient(({ selectedClient, rolesOptions }));
  return !roles.find(({ roleName }) => roleName === role)?.disabled;
};

export const filterByPrivileges = (userSettings, loggedInUserAssignments, isClientSelected) => {
  const allAssignments = loggedInUserAssignments.filter(isClientSelected);

  const assignments = {
    connections: allAssignments.filter(filterAssignmentsByType(assignmentTypes.CONNECTIONS)),
    companyCodes: allAssignments.filter(filterAssignmentsByType(assignmentTypes.COMPANY_CODES)),
    businesses: allAssignments.filter(filterAssignmentsByType(assignmentTypes.BUSINESSES)),
    countries: allAssignments.filter(filterAssignmentsByType(assignmentTypes.COUNTRIES)),
    businessUnits: allAssignments.filter(filterAssignmentsByType(assignmentTypes.BUSINESS_UNITS)),
    all: allAssignments.filter(filterAssignmentsByType(assignmentTypes.ALL)),
  };

  const findByAssignmentCode = code => ({ assignmentCode }) => assignmentCode === code;

  return {
    ...userSettings,
    connections: assignments.all.length
      ? userSettings.connections
      : userSettings.connections
        .filter(({ connectionName }) => assignments.connections.find(findByAssignmentCode(connectionName))),
    companyCodes: assignments.all.length
      ? userSettings.companyCodes
      : userSettings.companyCodes
        .filter(({ companyCode }) => assignments.companyCodes.find(findByAssignmentCode(companyCode))),
    countries: assignments.all.length
      ? userSettings.countries
      : userSettings.countries
        .filter(({ countryCode }) => assignments.countries.find(findByAssignmentCode(countryCode))),
    businessUnits: assignments.all.length
      ? userSettings.businessUnits
      : userSettings.businessUnits
        .filter(({ businessUnit }) => assignments.businessUnits.find(findByAssignmentCode(businessUnit))),
    businesses: assignments.all.length
      ? userSettings.businesses
      : userSettings.businesses
        .filter(({ businessCode }) => assignments.businesses.find(findByAssignmentCode(businessCode))),
  };
};

export const applyNewRoleAssignments = (
  checkedAssignments,
  newRoleAssignments,
  newRoleAssignmentsMaps,
  roleChangeDetails
) => {
  const { newRole, roleChanged, initialCheckedAssignments } = roleChangeDetails;
  const assignmentsToUse = roleChanged ? initialCheckedAssignments : checkedAssignments;

  const filteredAssignments = assignmentsToUse
    .filter(assignment => {
      const shouldInclude = (newRole !== userRoles.SUPER_USER && newRole !== userRoles.POWER_USER
        && newRole !== userRoles.USER) && assignment.assignmentCode !== ALL_ASSIGNMENTS_CODE;
      return shouldInclude;
    })
    .map(assignment => {
      const roleAssignment = newRoleAssignmentsMaps[getAssignmentKey(assignment)];
      if (roleAssignment) {
        return {
          ...assignment,
          privileges: uniq([
            ...assignment.privileges,
            ...roleAssignment.privileges,
          ]),
        };
      }
      return assignment;
    });

  return filteredAssignments;
};
